/**
 * Bottom Sheet Component
 *
 * Mobile-first bottom sheet that slides up from the bottom.
 * Tap overlay to dismiss. Uses CSS transitions (no framer-motion).
 */

import * as React from "react";

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Optional title shown as header */
  title?: string;
  /** Optional footer (e.g. action buttons) — stays visible, outside scroll */
  footer?: React.ReactNode;
  /** Custom class for content area */
  className?: string;
  /** Hint for drag handle (shows thin bar at top) */
  showHandle?: boolean;
  /** Compact mode: less padding, smaller header */
  compact?: boolean;
  /**
   * When true, sheet height is content-driven (auto).
   * Content area scrolls when long.
   */
  fitContent?: boolean;
  /** Optional id for the title (e.g. for aria-labelledby on dialog) */
  titleId?: string;
}

const DRAG_CLOSE_THRESHOLD_PX = 80;

export function BottomSheet({
  open,
  onOpenChange,
  children,
  title,
  footer,
  className,
  showHandle = true,
  compact = false,
  fitContent = false,
  titleId,
}: BottomSheetProps) {
  const [isExiting, setIsExiting] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [dragStartY, setDragStartY] = React.useState<number | null>(null);
  const [dragOffset, setDragOffset] = React.useState(0);
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const handleRef = React.useRef<HTMLDivElement>(null);
  const dragOffsetRef = React.useRef(0);
  const dragStartYRef = React.useRef<number | null>(null);
  dragOffsetRef.current = dragOffset;
  dragStartYRef.current = dragStartY;

  const handleOverlayClick = () => onOpenChange(false);

  // При фокусе на инпут/textarea — прокрутить его в зону над клавиатурой
  const handleContentFocusIn = React.useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const el = e.target;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT")
      ) {
        window.setTimeout(() => {
          (el as HTMLElement).scrollIntoView({
            block: "center",
            behavior: "smooth",
            inline: "nearest",
          });
        }, 350);
      }
    },
    []
  );

  // When open becomes true: mount and trigger enter (visible)
  React.useEffect(() => {
    if (open) {
      setIsExiting(false);
      setDragOffset(0);
      setDragStartY(null);
      const t = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
      return () => cancelAnimationFrame(t);
    }
  }, [open]);

  // When open becomes false: play exit then unmount
  const startExit = React.useCallback(() => {
    setIsExiting(true);
    setIsVisible(false);
  }, []);

  React.useEffect(() => {
    if (open) return;
    if (isExiting) return;
    startExit();
  }, [open, isExiting, startExit]);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== sheetRef.current) return;
    if (e.propertyName === "transform" && isExiting) {
      setIsExiting(false);
      onOpenChange(false);
    }
  };

  // Drag to close: capture on sheet so move/up always fire
  const handlePointerDownOnHandle = (e: React.PointerEvent) => {
    if (!showHandle || isExiting || !sheetRef.current) return;
    e.preventDefault();
    sheetRef.current.setPointerCapture(e.pointerId);
    dragStartYRef.current = e.clientY;
    setDragStartY(e.clientY);
    setDragOffset(0);
  };

  const handlePointerMoveOnSheet = (e: React.PointerEvent) => {
    const startY = dragStartYRef.current;
    if (startY === null) return;
    e.preventDefault();
    const dy = e.clientY - startY;
    const offset = Math.max(0, dy);
    dragOffsetRef.current = offset;
    setDragOffset(offset);
  };

  const handlePointerUpOnSheet = (e: React.PointerEvent) => {
    if (sheetRef.current) sheetRef.current.releasePointerCapture(e.pointerId);
    const startY = dragStartYRef.current;
    if (startY === null) return;
    dragStartYRef.current = null;
    setDragStartY(null);
    if (dragOffsetRef.current > DRAG_CLOSE_THRESHOLD_PX) {
      startExit();
    } else {
      setDragOffset(0);
    }
  };

  // Block touchmove on sheet during drag (passive: false)
  React.useEffect(() => {
    if (!open && !isExiting) return;
    const el = sheetRef.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      if (dragStartYRef.current !== null) e.preventDefault();
    };
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, [open, isExiting]);

  // Block touchstart on handle (passive: false)
  React.useEffect(() => {
    if (!open && !isExiting) return;
    const el = handleRef.current;
    if (!el) return;
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
    };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => el.removeEventListener("touchstart", onTouchStart);
  }, [open, isExiting]);

  const show = open || isExiting;
  if (!show) return null;

  const topInset =
    "calc(env(safe-area-inset-top, 0px) + var(--header-height, 48px) + var(--header-offset-buffer, 1px) + 8px)";

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId ?? undefined}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 z-0 bg-black/50 transition-opacity duration-200"
        style={{ opacity: isVisible && !isExiting ? 1 : 0 }}
        onClick={handleOverlayClick}
        aria-hidden
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        style={{
          top: fitContent ? "auto" : topInset,
          bottom: 0,
          maxHeight: fitContent ? "85vh" : undefined,
          transform:
            isVisible && !isExiting
              ? `translateY(${dragOffset}px)`
              : "translateY(100%)",
          transition:
            dragStartY === null
              ? "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)"
              : "none",
        }}
        className="z-10 absolute left-0 right-0 bg-white rounded-t-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={handleTransitionEnd}
        onPointerMove={handlePointerMoveOnSheet}
        onPointerUp={handlePointerUpOnSheet}
        onPointerCancel={handlePointerUpOnSheet}
      >
        {/* Drag handle */}
        {showHandle && (
          <div
            ref={handleRef}
            role="button"
            tabIndex={0}
            aria-label="Потянуть вниз, чтобы закрыть"
            style={{ touchAction: "none" }}
            onPointerDown={handlePointerDownOnHandle}
            className={`flex justify-center items-center shrink-0 cursor-grab active:cursor-grabbing select-none ${compact ? "py-2" : "py-3"}`}
          >
            <div className="w-10 h-1 rounded-full bg-slate-300 pointer-events-none" />
          </div>
        )}

        {/* Header (optional) */}
        {title && (
          <div className={`px-4 shrink-0 ${compact ? "pb-2" : "pb-3"}`}>
            <h3
              id={titleId}
              className={`font-semibold text-slate-900 ${compact ? "text-sm" : "text-base"}`}
            >
              {title}
            </h3>
          </div>
        )}

        {/* Content */}
        <div
          onFocusCapture={handleContentFocusIn}
          className={`overflow-y-auto overscroll-contain px-4 min-h-0 flex-1 ${compact ? "pb-2" : "pb-4"} ${className ?? ""}`}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="shrink-0 border-t border-slate-100 bg-white px-4 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+var(--tab-bar-content,52px)+16px)]"
          >
            {footer}
          </div>
        )}

        {/* Bottom safe area when no footer */}
        {!footer && (
          <div className="shrink-0 pb-[calc(env(safe-area-inset-bottom,0px)+var(--tab-bar-content,52px)+16px)]" />
        )}
      </div>
    </div>
  );
}
